import { combineLatest, from, Observable, switchMap, take } from 'rxjs';
import { CardService } from '@libs/core/api';
import { ProjectsService } from '@front-end/core/services';
import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CompactEncrypt, importJWK } from 'jose';

@UntilDestroy()
@Injectable()
export class PinEditService {
  constructor(
    private readonly projectsService: ProjectsService,
    private readonly cardService: CardService
  ) {}

  public updatePin(newPin: string): Observable<void> {
    const encryptPin$ = from(this.encryptPin(newPin));

    const card$ = this.projectsService.selectedProject$.pipe(
      take(1),
      switchMap((project) =>
        this.cardService.getCardByProjectId({ projectId: project.id as number })
      )
    );

    return combineLatest([card$, encryptPin$]).pipe(
      switchMap(([card, encryptedPin]) =>
        this.cardService.updatePin({
          body: {
            cardId: card?.cardId ?? '',
            accountId: card?.accountId ?? '',
            encryptedPin,
          },
        })
      )
    );
  }

  private async encryptPin(pin: string): Promise<string> {
    const keyData = await fetch('https://issuing-key.stripe.com/v1/keys').then(
      (r) => r.json()
    );
    const alg = keyData.jwk.alg ?? 'RSA-OAEP';
    const publicKey = await importJWK(keyData.jwk, alg);
    return await new CompactEncrypt(new TextEncoder().encode(pin))
      .setProtectedHeader({
        alg: 'RSA-OAEP',
        enc: 'A128CBC-HS256',
        kid: keyData.key_id,
      })
      .encrypt(publicKey);
  }
}
